/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Toast } from 'sharedUi/Toast';
import { Button } from 'sharedUi/Button';
import { Dropdown } from 'sharedUi/Dropdown';
import { RefreshButton } from 'sharedUi/RefreshButton';
import { useSecureNavigation } from '../../hooks/useSecureNavigation';
import { useDebounce } from '../../hooks/useDebounce';
import { CHANNEL_OPTIONS } from '../../components/notifications/notification-filter-options';
import {
  notificationMastersService, type NotificationMaster,
} from '../../services/notification-masters.service';
import { notificationTypesService } from '../../services/notification-types.service';
import { totalOf, NEWEST_FIRST, sortParams, type SortState } from '../../services/notification-engine.types';
import { formatTimestamp } from 'sharedUi/dateUtils';
import { extractErrorMessage } from '../../utils/error-handler';
import PermissionGuard from '../../components/guards/PermissionGuard';
import { JOB_NAMES } from '../../constants/jobNames';
import PageHeader from '../../components/common/page-header';
import SaasDataTable, { type SaasColumn } from '../../components/common/saas-data-table';
import { ProjectCell } from '../../components/common/project-cell';
import { useAccessibleProjects } from '../../hooks/useProjectScope';
import ProjectFilter from '../../components/common/project-filter';
import ShowCoreToggle from '../../components/common/show-core-toggle';
import Icon from '../../components/common/icon';
import DeleteConfirmDialog from '../../components/dialogs/delete-confirm-dialog';
import { useCanCreate, useCanUpdate, useCanDelete } from '../../hooks/usePermissions';
import ChannelIcons from '../../components/common/channel-icons';

/**
 * The notification engine's notification masters.
 *
 * `embedded` drops the page header so the screen can sit as a tab inside the
 * Notifications group, which already names itself — the same arrangement the
 * live master list uses.
 */
const NotificationMasterList = ({ embedded = false }: { embedded?: boolean }) => {
  const { navigateToCreate, navigateToView, navigateToEdit } = useSecureNavigation({
    entity: 'notification', basePath: '/notification-masters',
  });
  const toast = useRef<Toast>(null);
  const canCreate = useCanCreate(JOB_NAMES.NOTIFICATION_MASTER);
  const canUpdate = useCanUpdate(JOB_NAMES.NOTIFICATION_MASTER);
  const canDelete = useCanDelete(JOB_NAMES.NOTIFICATION_MASTER);

  // Which project each row belongs to. The COLUMN always shows — "this project"
  // versus the shared "Global" rows is worth seeing even to someone with one
  // project — while the filter beside it only appears when there is a choice.
  const { projects, canScopeByProject } = useAccessibleProjects();

  const [rows, setRows] = useState<NotificationMaster[]>([]);
  const [types, setTypes] = useState<Record<string, { code: string; channels: string[] }>>({});
  const [loading, setLoading] = useState(true);
  const [first, setFirst] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);
  // Server-side sort: the table asks, the API orders. Sorting in the browser
  // would only order the rows already fetched, which is not what a sort control
  // appears to promise. Newest first until the viewer picks a column.
  const [sort, setSort] = useState<SortState>(NEWEST_FIRST);
  const [toDelete, setToDelete] = useState<NotificationMaster | null>(null);

  // Filters. A notification has no code or channels of its own, so the type
  // dropdown filters by the type it points at and the channel filter resolves
  // through the type on the server; search matches its description.
  const [search, setSearch] = useState('');
  const [typeId, setTypeId] = useState<string | null>(null);
  const [channel, setChannel] = useState<string | null>(null);
  const [appId, setAppId] = useState<string | null>(null);
  const [showCore, setShowCore] = useState(false);
  const debouncedSearch = useDebounce(search, 400);

  const showError = useCallback((m: string) => toast.current?.show({ severity: 'error', summary: 'Error', detail: m, life: 8000 }), []);
  const showOk = useCallback((m: string) => toast.current?.show({ severity: 'success', summary: 'Success', detail: m, life: 3000 }), []);

  const fetchRows = useCallback(async (skip = 0, limit = pageSize, sorting: SortState = sort) => {
    try {
      setLoading(true);
      const resp = await notificationMastersService.list({
        skip, limit, ...sortParams(sorting),
        ...(debouncedSearch.trim() ? { search: debouncedSearch.trim() } : {}),
        ...(typeId ? { notificationTypeId: typeId } : {}),
        ...(channel ? { channel } : {}),
        ...(appId ? { appId } : {}),
        include_global: showCore,
      });
      setRows(resp.data || []);
      setTotal(totalOf(resp));
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to load notifications'));
    } finally {
      setLoading(false);
    }
  }, [pageSize, sort, showError, debouncedSearch, typeId, channel, appId, showCore]);

  // A notification inherits its code from its type, so without this the list
  // could only show an id where the identifier belongs.
  const fetchTypes = useCallback(async () => {
    try {
      const resp = await notificationTypesService.list({ skip: 0, limit: 200 });
      setTypes(Object.fromEntries((resp.data || []).map((t) => [t._id, { code: t.notificationCode, channels: t.channels || [] }])));
    } catch {
      /* The list is still readable without codes; ids are shown instead. */
    }
  }, []);

  // Types load once — they populate both the Type column and the Type filter.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchTypes(); }, []);

  // First load, and again on any filter change — always from page one, so a
  // narrowed result cannot strand the viewer on a page that no longer exists.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setFirst(0); fetchRows(0, pageSize); }, [debouncedSearch, typeId, channel, appId, showCore]);

  // The type dropdown lists the same types the rows resolve their code from,
  // sorted by code so the menu reads alphabetically.
  const typeOptions = useMemo(
    () => Object.entries(types)
      .map(([id, t]) => ({ label: t.code, value: id }))
      .sort((a, b) => a.label.localeCompare(b.label)),
    [types],
  );

  const handlePage = (e: any) => { setFirst(e.first); setPageSize(e.rows); fetchRows(e.first, e.rows); };

  const handleSort = (e: any) => {
    const next: SortState = { field: e.sortField || null, order: e.sortOrder || null };
    setSort(next);
    setFirst(0);
    fetchRows(0, pageSize, next);
  };

  const handleDelete = async () => {
    if (!toDelete) return;
    try {
      await notificationMastersService.softDelete(toDelete._id);
      setToDelete(null);
      showOk('Notification deleted');
      fetchRows(first, pageSize);
    } catch (e) {
      showError(extractErrorMessage(e, 'Failed to delete notification'));
    }
  };

  const columns: SaasColumn<NotificationMaster>[] = useMemo(() => [
    {
      field: 'notificationTypeId', header: 'Type',
      body: (r) => <span className="font-mono text-xs">{types[String(r.notificationTypeId)]?.code || String(r.notificationTypeId)}</span>,
    },
    { field: 'description', header: 'Description', sortable: true, body: (r) => <span className="text-sm">{r.description || '-'}</span> },
    // From the type — the notification carries no list of its own.
    { field: 'channels', header: 'Channels', body: (r) => <ChannelIcons channels={types[String(r.notificationTypeId)]?.channels} /> },
    { field: 'recipients', header: 'Recipients', body: (r) => <span className="text-xs">{(r.recipients || []).length}</span> },
    { field: 'parameters', header: 'Parameters', body: (r) => <span className="text-xs">{(r.parameters || []).length}</span> },
    {
      field: 'appId', header: 'Project', style: { minWidth: 150 },
      body: (r: NotificationMaster) => <ProjectCell appId={r.appId} projects={projects} />,
    },
    { field: 'createdAt', header: 'Created', sortable: true, body: (r) => <span className="text-xs text-gray-500 whitespace-nowrap">{r.createdAt ? formatTimestamp(r.createdAt) : '-'}</span> },
    {
      field: 'actions', header: 'Actions', align: 'center', hideable: false,
      body: (r) => (
        <div className="flex gap-1 justify-center">
          <button aria-label="View" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToView(r._id)} title="View">
            <Icon name="eye" size={17} className="text-green-600 dark:text-green-400" />
          </button>
          {canUpdate && <button aria-label="Edit" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => navigateToEdit(r._id)} title="Edit">
            <Icon name="pencil" size={17} className="text-blue-600 dark:text-blue-400" />
          </button>}
          {canDelete && <button aria-label="Delete" className="p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700" onClick={() => setToDelete(r)} title="Delete">
            <Icon name="trash" size={17} className="text-red-600 dark:text-red-400" />
          </button>}
        </div>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [canUpdate, canDelete, types, canScopeByProject, projects]);

  return (
    <PermissionGuard jobName={JOB_NAMES.NOTIFICATION_MASTER}>
      <Toast ref={toast} position="top-right" />
      <div className={embedded ? '' : 'p-6'} data-testid="SCR-NotificationMaster-List">
        {!embedded && (
          <PageHeader icon="pi pi-bell" title="Notification Master" subtitle="What gets sent — its type, channels, templates and who it goes to" testId="NotificationMaster.Header" />
        )}
        <div className={embedded ? '' : 'bg-surface rounded-xl shadow-sm border border-line overflow-hidden'}>
          <SaasDataTable
            tableId="notification-master-engine" columns={columns} data={rows} loading={loading} dataKey="_id"
            emptyMessage="No notifications found" first={first} rows={pageSize} totalRecords={total}
            onPage={handlePage} rowsPerPageOptions={[5, 10, 25, 50]} sortField={sort.field || undefined} sortOrder={sort.order} onSort={handleSort} onFilter={() => {}} testId="NotificationMasterList"
            globalSearch={search} onGlobalSearchChange={setSearch} globalSearchPlaceholder="Search description..."
            toolbarStart={
              <div className="flex flex-wrap items-center gap-2">
                <ProjectFilter value={appId} onChange={setAppId} testId="NotificationMasterList.Filter.Project" />
                <Dropdown value={typeId} options={typeOptions} onChange={(e: any) => setTypeId(e.value || null)} placeholder="Type" showClear filter style={{ minWidth: '170px' }} data-testid="NotificationMasterList.Filter.Type" />
                <Dropdown value={channel} options={CHANNEL_OPTIONS} onChange={(e: any) => setChannel(e.value || null)} placeholder="Channel" showClear style={{ minWidth: '150px' }} data-testid="NotificationMasterList.Filter.Channel" />
                <ShowCoreToggle checked={showCore} onChange={setShowCore} testId="NotificationMasterList.Toggle.ShowCore" />
              </div>
            }
            toolbarEnd={<>
              <span className="text-sm text-gray-400 whitespace-nowrap">{total} {total === 1 ? 'record' : 'records'}</span>
              <RefreshButton onClick={() => fetchRows(first, pageSize)} loading={loading} testId="NotificationMasterList.Refresh" />
              {canCreate && <Button label="Create New Notification Master" icon="pi pi-plus" onClick={() => navigateToCreate()} size="small" />}
            </>}
          />
        </div>
      </div>
      <DeleteConfirmDialog visible={!!toDelete} onCancel={() => setToDelete(null)} onDelete={handleDelete}
        message={`Delete this notification (${types[String(toDelete?.notificationTypeId)]?.code || 'unknown type'})?`} testId="NotificationMasterList.Delete" />
    </PermissionGuard>
  );
};

export default NotificationMasterList;
